import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Reveal, waLink, EASE, fbLead } from "./primitives";
import { motion } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROFESSIONS = ["Founder", "C-Suite", "Business", "Investor", "Media", "Athlete", "Celebrity", "Family Business", "Legacy Family", "Promoter", "Other"];

export default function Application() {
  const [form, setForm] = useState({ full_name: "", profession: "", profession_other: "", linkedin: "", instagram: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const professionLabel = form.profession === "Other" && form.profession_other ? form.profession_other : form.profession;

  const buildWaExtra = () => {
    let extra = "";
    if (form.full_name) extra += `\nName: ${form.full_name}`;
    if (professionLabel) extra += `\nProfession: ${professionLabel}`;
    if (form.phone) extra += `\nPhone: ${form.phone}`;
    if (form.linkedin) extra += `\nLinkedIn: ${form.linkedin}`;
    if (form.instagram) extra += `\nInstagram: ${form.instagram}`;
    return extra;
  };

  const persist = async () => {
    if (!form.full_name && !form.phone) return;
    try {
      await axios.post(`${API}/applications`, form);
    } catch (e) {
      console.error("save failed", e);
    }
  };

  const onWhatsApp = async (e) => {
    // fire Meta Pixel Lead, save quietly, then let anchor navigation proceed
    fbLead("whatsapp");
    persist();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone) {
      toast.error("Please share your name and phone number.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/applications`, form);
      fbLead("form");
      toast.success("Received. We will be in touch, discreetly.");
      setForm({ full_name: "", profession: "", profession_other: "", linkedin: "", instagram: "", phone: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try WhatsApp instead.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "field-line w-full py-3 text-lg";

  return (
    <section id="contact" className="lux py-28 md:py-44 px-6 md:px-10" style={{ backgroundColor: "var(--forest)" }} data-testid="application-section">
      <div className="mx-auto max-w-3xl">
        <Reveal>
          <div className="text-center">
            <span className="eyebrow" style={{ color: "var(--brass)" }}>The Application</span>
            <h2 className="font-heading font-light leading-[1.05] tracking-[-0.01em] mt-6 text-[2.2rem] md:text-[3.4rem]" style={{ color: "var(--ivory)" }}>
              If this feels familiar, we should speak.
            </h2>
            <p className="font-body italic text-xl md:text-2xl mt-6" style={{ color: "rgba(245,238,223,0.7)" }}>
              This was not built for everyone. It was built for the few who recognise it at once.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <form onSubmit={onSubmit} className="mt-16 space-y-9" data-testid="application-form">
            <div>
              <label className="eyebrow block mb-3" style={{ color: "var(--brass)" }}>Full Name</label>
              <input className={inputCls} value={form.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="Your name" data-testid="input-full-name" />
            </div>

            <div>
              <label className="eyebrow block mb-3" style={{ color: "var(--brass)" }}>Profession</label>
              <Select value={form.profession} onValueChange={(v) => set("profession", v)}>
                <SelectTrigger className="field-line w-full py-6 text-lg justify-between" data-testid="select-profession" style={{ boxShadow: "none" }}>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent className="rounded-none border-0" style={{ background: "var(--olive)", color: "var(--ivory)" }}>
                  {PROFESSIONS.map((p) => (
                    <SelectItem key={p} value={p} className="font-label rounded-none cursor-pointer" data-testid={`profession-${p.toLowerCase()}`}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {form.profession === "Other" && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} transition={{ duration: 0.4, ease: EASE }}>
                <label className="eyebrow block mb-3" style={{ color: "var(--brass)" }}>Tell us your field</label>
                <input className={inputCls} value={form.profession_other} onChange={(e) => set("profession_other", e.target.value)} placeholder="Your field" data-testid="input-profession-other" />
              </motion.div>
            )}

            <div className="grid md:grid-cols-2 gap-9">
              <div>
                <label className="eyebrow block mb-3" style={{ color: "var(--brass)" }}>LinkedIn <span style={{ textTransform: "none", letterSpacing: 0 }}>(optional)</span></label>
                <input className={inputCls} value={form.linkedin} onChange={(e) => set("linkedin", e.target.value)} placeholder="Profile URL" data-testid="input-linkedin" />
              </div>
              <div>
                <label className="eyebrow block mb-3" style={{ color: "var(--brass)" }}>Instagram <span style={{ textTransform: "none", letterSpacing: 0 }}>(optional)</span></label>
                <input className={inputCls} value={form.instagram} onChange={(e) => set("instagram", e.target.value)} placeholder="@handle" data-testid="input-instagram" />
              </div>
            </div>

            <div>
              <label className="eyebrow block mb-3" style={{ color: "var(--brass)" }}>Phone Number</label>
              <input className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+91" data-testid="input-phone" />
            </div>

            <div className="pt-6 flex flex-col items-center gap-6">
              <a
                href={waLink(buildWaExtra())}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onWhatsApp}
                className="w-full text-center font-label text-[0.82rem] tracking-[0.24em] uppercase px-9 py-5 font-normal"
                style={{ backgroundColor: "var(--ivory)", color: "var(--forest)", transition: "background-color 200ms ease, color 200ms ease" }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "var(--sage)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "var(--ivory)"; }}
                data-testid="apply-whatsapp-btn"
              >
                Engage on WhatsApp
              </a>
              <button
                type="submit"
                disabled={submitting}
                className="font-label text-[0.72rem] tracking-[0.2em] uppercase link-underline font-light disabled:opacity-50"
                style={{ color: "rgba(245,238,223,0.7)" }}
                data-testid="submit-application-btn"
              >
                {submitting ? "Sending" : "Or submit quietly instead"}
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
